<template>
  <div class="py-8">
    <div class="w-full">
      <!-- Header -->
      <div class="mb-5 flex items-center justify-between">
        <div class="flex items-center gap-2 text-2xl font-bold">
          <el-image src="/common/star.png"></el-image>
          <div class="dark:text-white">
            {{ $t("matchList.todayCount", { count: matches.length }) }}
          </div>
        </div>
        <div class="flex gap-2">
          <div
            class="flex size-8 cursor-pointer items-center justify-center rounded-full"
            @click="scrollLeft"
          >
            <el-icon color="#6B6B6B">
              <ArrowLeftBold />
            </el-icon>
          </div>
          <div
            class="flex size-8 cursor-pointer items-center justify-center rounded-full"
            @click="scrollRight"
          >
            <el-icon color="#6B6B6B">
              <ArrowRightBold />
            </el-icon>
          </div>
        </div>
      </div>

      <!-- 比赛列表 -->
      <div class="overflow-x-auto">
        <div
          ref="matchListRef"
          class="scrollbar-hide flex gap-4 overflow-hidden pb-2"
        >
          <div
            v-for="match in matches"
            :key="match.id"
            class="match-card dark:solid w-[272px] shrink-0 cursor-pointer bg-white dark:border dark:border-[#575759] dark:bg-[#2C2C2C]"
            @click="selectMatch(match)"
          >
            <!-- 卡片头部 -->
            <div
              class="solid mx-2 my-3 flex items-center justify-between border-b border-[#575759] px-4 pt-3 pb-2"
            >
              <div class="text-xs font-medium text-gray-400">
                {{ match.league }}{{ match.round ? ` 第${match.round}轮` : "" }}
              </div>
              <div class="flex items-center gap-1.5 text-orange-500">
                <div class="text-xs font-medium">直播中</div>
                <el-image src="/common/hot.png"></el-image>
              </div>
            </div>

            <!-- 卡片内容 -->
            <div class="flex items-center justify-between gap-4 px-4 pb-4">
              <!-- 主队 -->
              <div class="flex min-w-0 flex-1 flex-col items-center gap-2">
                <div class="flex h-16 w-16 items-center justify-center">
                  <img
                    v-if="match.homeTeam.logo"
                    :src="match.homeTeam.logo"
                    :alt="match.homeTeam.name"
                    class="h-full w-full object-contain"
                  />
                  <el-image src="home.png"></el-image>
                </div>
                <div
                  class="w-full truncate text-center text-sm font-medium dark:text-white"
                >
                  {{ match.homeTeam.name }}
                </div>
              </div>

              <!-- 中间比分区 -->
              <div class="flex shrink-0 flex-col items-center gap-0">
                <div class="text-3xl font-bold dark:text-white">
                  {{ match.homeTeam.score || "0" }} :
                  {{ match.awayTeam.score || "0" }}
                </div>
              </div>

              <!-- 客队 -->
              <div class="flex min-w-0 flex-1 flex-col items-center gap-2">
                <div class="flex h-16 w-16 items-center justify-center">
                  <img
                    v-if="match.awayTeam.logo"
                    :src="match.awayTeam.logo"
                    :alt="match.awayTeam.name"
                    class="h-full w-full object-contain"
                  />
                  <el-image src="away.png"></el-image>
                </div>
                <div
                  class="w-full truncate text-center text-sm font-medium dark:text-white"
                >
                  {{ match.awayTeam.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ArrowLeftBold, ArrowRightBold } from "@element-plus/icons-vue";

interface Team {
  name: string;
  logo: string;
  score: string;
}

interface Match {
  id: string;
  league: string;
  round?: number;
  homeTeam: Team;
  awayTeam: Team;
  time: string;
  status: "live" | "upcoming" | "finished";
}

interface Props {
  matches: Match[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  matchSelect: [match: Match];
}>();

const matchListRef = ref<HTMLElement>();
const canScrollLeft = ref(false);
const canScrollRight = ref(true);

// 选择比赛
const selectMatch = (match: Match) => {
  emit("matchSelect", match);
};

// 滚动到左侧
const scrollLeft = () => {
  if (matchListRef.value) {
    matchListRef.value.scrollBy({
      left: -400,
      behavior: "smooth",
    });
  }
};

// 滚动到右侧
const scrollRight = () => {
  if (matchListRef.value) {
    matchListRef.value.scrollBy({
      left: 400,
      behavior: "smooth",
    });
  }
};

// 检查滚动状态
const checkScroll = () => {
  if (matchListRef.value) {
    const el = matchListRef.value;
    canScrollLeft.value = el.scrollLeft > 0;
    canScrollRight.value = el.scrollLeft < el.scrollWidth - el.clientWidth - 10;
  }
};

// 获取状态文本
const getStatusText = (status: string) => {
  switch (status) {
    case "live":
      return "正在直播";
    case "upcoming":
      return "未开赛";
    case "finished":
      return "已结束";
    default:
      return "";
  }
};

// 获取 Element Plus Tag 类型
const getStatusType = (status: string) => {
  switch (status) {
    case "live":
      return "danger";
    case "upcoming":
      return "info";
    case "finished":
      return "warning";
    default:
      return "info";
  }
};

onMounted(() => {
  if (matchListRef.value) {
    matchListRef.value.addEventListener("scroll", checkScroll);
    checkScroll();
  }
});

onBeforeUnmount(() => {
  if (matchListRef.value) {
    matchListRef.value.removeEventListener("scroll", checkScroll);
  }
});
</script>

<style scoped></style>
